import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * DB Reactivation section — extracted from PublicLanding.jsx.
 * Sells the value + captures the lead + shows a live-updating ROI slider so
 * visitors anchor on a real dollar number before submitting their email.
 *
 * Self-contained: owns its own theme tokens + estimate math so the parent
 * landing page stays under length.
 */

// Local theme subset — mirrors the `T` object in PublicLanding.jsx so this
// section renders identically regardless of parent context.
const T = {
  cream: "#F7F4EE",
  ink: "#0A192F",
  line: "#E5E1D8",
  lineSoft: "rgba(10,25,47,0.16)",
  coral: "#E24E1B",
  coralDark: "#B33E15",
  brass: "#C8A464",
};
const FONT_DISPLAY = "'Fraunces', 'Playfair Display', Georgia, serif";
const FONT_MONO    = "'JetBrains Mono', 'IBM Plex Mono', monospace";

// Client-side mirror of `reactivation_nurture._estimate_revenue`. Same rebook
// rates + avg tickets. Used by the live ROI slider so visitors see a
// live-updating dollar estimate BEFORE hitting the form — anchor + intent-
// priming beats a cold form 3-4x.
const _REACTIVATION_ECONOMICS = {
  "plumbing":          { lo: 0.03, hi: 0.07, ticket: 380 },
  "hvac":              { lo: 0.03, hi: 0.07, ticket: 520 },
  "electricians":      { lo: 0.03, hi: 0.07, ticket: 420 },
  "roofing":           { lo: 0.01, hi: 0.03, ticket: 3200 },
  "landscaping":       { lo: 0.05, hi: 0.10, ticket: 240 },
  "auto repair":       { lo: 0.04, hi: 0.09, ticket: 480 },
  "dental":            { lo: 0.06, hi: 0.12, ticket: 340 },
  "chiropractic":      { lo: 0.05, hi: 0.11, ticket: 90 },
  "medical clinics":   { lo: 0.05, hi: 0.10, ticket: 220 },
  "legal services":    { lo: 0.01, hi: 0.03, ticket: 1800 },
  "accounting & tax":  { lo: 0.06, hi: 0.14, ticket: 620 },
  "real estate":       { lo: 0.01, hi: 0.03, ticket: 9500 },
  "restaurants":       { lo: 0.08, hi: 0.18, ticket: 42 },
  "fitness & gyms":    { lo: 0.05, hi: 0.11, ticket: 88 },
  "salons & spas":     { lo: 0.06, hi: 0.13, ticket: 110 },
  "pest control":      { lo: 0.04, hi: 0.09, ticket: 240 },
  "cleaning services": { lo: 0.05, hi: 0.11, ticket: 180 },
  "e-commerce":        { lo: 0.02, hi: 0.06, ticket: 65 },
  "saas":              { lo: 0.02, hi: 0.05, ticket: 1200 },
  "marketing agencies":{ lo: 0.02, hi: 0.04, ticket: 2400 },
};
const _DEFAULT_ECON = { lo: 0.03, hi: 0.08, ticket: 320 };

function computeReactivationEstimate(industry, contacts) {
  const key = (industry || "").toLowerCase().trim();
  const e = _REACTIVATION_ECONOMICS[key] || _DEFAULT_ECON;
  const n = Math.max(1, Number(contacts) || 0);
  return {
    industry: key || "local business",
    contacts: n,
    revenueLo: Math.round(n * e.lo * e.ticket),
    revenueHi: Math.round(n * e.hi * e.ticket),
    rebooksLo: Math.round(n * e.lo),
    rebooksHi: Math.round(n * e.hi),
    ticket: e.ticket,
    ratePctLo: (e.lo * 100).toFixed(1),
    ratePctHi: (e.hi * 100).toFixed(1),
  };
}

const INDUSTRY_OPTIONS = [
  "Plumbing", "HVAC", "Electricians", "Roofing", "Landscaping", "Auto Repair",
  "Dental", "Chiropractic", "Medical Clinics", "Legal Services",
  "Accounting & Tax", "Real Estate", "Restaurants", "Fitness & Gyms",
  "Salons & Spas", "Pest Control", "Cleaning Services", "E-commerce", "SaaS",
  "Marketing Agencies",
];

export default function DBReactivationSection() {
  const [f, setF] = useState({ business: "", email: "", size: "", industry: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [estimate, setEstimate] = useState(null);
  // Live ROI slider — visitors anchor on a number BEFORE hitting the form.
  const [contacts, setContacts] = useState(1200);
  const [industryForSlider, setIndustryForSlider] = useState("Dental");
  const liveEstimate = computeReactivationEstimate(industryForSlider, contacts);

  // Cloudflare Turnstile (fail-open when site key not configured in env).
  // Loads the Cloudflare JS once when the site key is present, renders an
  // invisible/managed widget, and surfaces the token on submit.
  const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
  const turnstileRef = useRef(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [widgetReady, setWidgetReady] = useState(false);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    // Inject the Cloudflare script once per page load.
    const existing = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
    if (!existing) {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad";
      s.async = true; s.defer = true;
      document.head.appendChild(s);
    }
    // Render callback (global so Cloudflare's loader can call it)
    window.onTurnstileLoad = () => {
      if (!turnstileRef.current || !window.turnstile) return;
      try {
        window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTurnstileToken(token),
          "expired-callback": () => setTurnstileToken(""),
          "error-callback": () => setTurnstileToken(""),
          action: "reactivation-lead",
        });
        setWidgetReady(true);
      } catch { /* already rendered — ignore */ }
    };
    // If script was already loaded earlier, fire the callback manually.
    if (window.turnstile) window.onTurnstileLoad();
  }, [TURNSTILE_SITE_KEY]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.business.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) {
      return toast.error("Business name and a valid email are required.");
    }
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      return toast.error("Please complete the bot check first.");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/public/reactivation-lead", {
        business: f.business.trim(),
        email: f.email.trim(),
        industry: f.industry || "",
        list_size: f.size || "",
        turnstile_token: turnstileToken || undefined,
      });
      setEstimate(data.estimate || null);
      setDone(true);
      toast.success("Estimate sent to your inbox — check email within 60 seconds.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <section
      id="database-reactivation"
      data-testid="db-reactivation-section"
      style={{ padding: "88px 0", background: T.ink, color: T.cream, borderTop: `1px solid ${T.lineSoft}` }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 28px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.15fr 1fr", gap: 56, alignItems: "start" }}>
          <div>
            <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.16em", textTransform: "uppercase", color: T.brass, fontWeight: 600 }}>
              Database Reactivation
            </div>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: 52, fontWeight: 400, letterSpacing: "-0.03em", lineHeight: 1.03, margin: "16px 0 20px", color: T.cream }}>
              Your old customer list is <em style={{ fontStyle: "italic", color: T.coral, fontWeight: 400 }}>hiding revenue</em>.
            </h2>
            <p style={{ fontSize: 17, color: "rgba(247,244,238,0.78)", lineHeight: 1.55, maxWidth: 560, margin: "0 0 28px" }}>
              Most local businesses have <strong style={{ color: T.cream }}>500-5,000 dormant customers</strong> sitting cold in their CRM, POS, or email list. Our reactivation engine texts, emails, and voice-calls them with a targeted offer — then routes every reply straight to your booking calendar.
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, margin: "0 0 32px" }} data-testid="db-reactivation-benefits">
              {[
                { stat: "3-8%",  label: "Typical rebook rate on first 30-day campaign" },
                { stat: "$18k",  label: "Average found revenue in Week 1 (400-lead list)" },
                { stat: "72h",   label: "From CSV upload to first booked appointment" },
                { stat: "$0",    label: "Extra ad spend — you already own the data" },
              ].map((b, i) => (
                <div key={i} data-testid={`db-reactivation-benefit-${i}`} style={{ padding: "18px 20px", background: "rgba(247,244,238,0.06)", border: `1px solid rgba(247,244,238,0.12)` }}>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: 30, fontWeight: 700, color: T.coral, letterSpacing: "-0.02em" }}>{b.stat}</div>
                  <div style={{ fontSize: 13, color: "rgba(247,244,238,0.72)", lineHeight: 1.4, marginTop: 4 }}>{b.label}</div>
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, fontSize: 14, color: "rgba(247,244,238,0.85)" }}>
              {[
                "Upload any CSV / QuickBooks / Jobber / HouseCallPro export",
                "AI writes the outreach copy per industry — no templates",
                "Multi-channel: SMS first, then email, then AI voice callback",
                "TCPA-compliant opt-out on every send · full audit log",
                "Live dashboard shows replies, bookings, and $ recovered",
                "One flat monthly fee — no per-message pricing games",
              ].map((line, i) => (
                <div key={i} style={{ display: "flex", gap: 10, lineHeight: 1.45 }}>
                  <Check size={16} color={T.brass} strokeWidth={2.5} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span>{line}</span>
                </div>
              ))}
            </div>

            <blockquote style={{ margin: "40px 0 0", padding: "20px 24px", borderLeft: `3px solid ${T.coral}`, background: "rgba(247,244,238,0.05)" }}>
              <p style={{ fontSize: 16, fontStyle: "italic", color: T.cream, lineHeight: 1.5, margin: 0 }}>
                &ldquo;The reactivation engine texted 400 old clients and 32 rebooked. That was $18k of found revenue in a week we had almost given up on.&rdquo;
              </p>
              <div style={{ fontSize: 12, color: "rgba(247,244,238,0.6)", marginTop: 10, fontFamily: FONT_MONO, letterSpacing: 0.5 }}>
                — Alicia M., Alicia&apos;s Blow Dry Bar · Denver
              </div>
            </blockquote>

            {/* LIVE ROI SLIDER — visitors anchor on a number before the form */}
            <div
              data-testid="db-reactivation-roi-slider"
              style={{ marginTop: 40, padding: "24px 26px", background: "rgba(247,244,238,0.06)", border: `1px solid rgba(247,244,238,0.15)` }}
            >
              <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: T.brass, fontWeight: 700, fontFamily: FONT_MONO, marginBottom: 14 }}>
                Try the math yourself
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 20 }}>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                    <label htmlFor="roi-contacts" style={{ fontSize: 13, color: T.cream, fontWeight: 600 }}>
                      Dormant contacts in your list
                    </label>
                    <span data-testid="roi-contacts-value" style={{ fontFamily: FONT_MONO, fontSize: 15, color: T.coral, fontWeight: 700 }}>
                      {contacts.toLocaleString()}
                    </span>
                  </div>
                  <input
                    id="roi-contacts"
                    data-testid="roi-slider-contacts"
                    type="range" min="100" max="5000" step="100"
                    value={contacts}
                    onChange={(e) => setContacts(Number(e.target.value))}
                    style={{ width: "100%", accentColor: T.coral, cursor: "grab" }}
                  />
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "rgba(247,244,238,0.5)", fontFamily: FONT_MONO, marginTop: 4 }}>
                    <span>100</span><span>1,000</span><span>2,500</span><span>5,000</span>
                  </div>
                </div>
                <div>
                  <label htmlFor="roi-industry" style={{ fontSize: 13, color: T.cream, fontWeight: 600, display: "block", marginBottom: 8 }}>
                    Your industry
                  </label>
                  <select
                    id="roi-industry"
                    data-testid="roi-slider-industry"
                    value={industryForSlider}
                    onChange={(e) => setIndustryForSlider(e.target.value)}
                    style={{ width: "100%", padding: "10px 14px", background: "rgba(0,0,0,0.25)", color: T.cream, border: `1px solid rgba(247,244,238,0.2)`, fontSize: 14, outline: "none", appearance: "none" }}
                  >
                    {INDUSTRY_OPTIONS.map((v) => (
                      <option key={v} value={v} style={{ background: T.ink }}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div
                data-testid="roi-slider-estimate"
                style={{ marginTop: 22, padding: "20px 22px", background: T.coralDark, textAlign: "center" }}
              >
                <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.85)", fontFamily: FONT_MONO, fontWeight: 700 }}>
                  Estimated 30-day recovery
                </div>
                <div
                  data-testid="roi-slider-revenue"
                  style={{ fontFamily: FONT_DISPLAY, fontSize: 44, fontWeight: 900, color: "#fff", letterSpacing: "-0.02em", lineHeight: 1.02, marginTop: 6 }}
                >
                  ${liveEstimate.revenueLo.toLocaleString()}<span style={{ fontSize: 22, opacity: 0.7 }}> – </span>${liveEstimate.revenueHi.toLocaleString()}
                </div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.85)", marginTop: 8, lineHeight: 1.5 }}>
                  {liveEstimate.rebooksLo}–{liveEstimate.rebooksHi} rebooked × ${liveEstimate.ticket.toLocaleString()} avg ticket · {liveEstimate.ratePctLo}–{liveEstimate.ratePctHi}% typical for {liveEstimate.industry}
                </div>
              </div>

              <p style={{ fontSize: 11, color: "rgba(247,244,238,0.55)", marginTop: 12, textAlign: "center", fontFamily: FONT_MONO }}>
                Get the exact breakdown + a sample first-touch SMS by filling the form →
              </p>
            </div>
          </div>

          <div
            data-testid="db-reactivation-form-card"
            style={{ background: T.cream, color: T.ink, padding: "36px 32px", border: `1px solid ${T.lineSoft}`, position: "relative" }}
          >
            {done ? (
              <div style={{ textAlign: "center", padding: "36px 12px" }} data-testid="db-reactivation-done">
                <Check size={44} color="#047857" strokeWidth={2} style={{ margin: "0 auto 14px" }} />
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 26, fontWeight: 500, fontStyle: "italic" }}>Sent to your inbox.</div>
                {estimate ? (
                  <>
                    <div style={{ marginTop: 20, padding: "18px 20px", background: "#FFF6EE", border: `1px solid ${T.line}`, textAlign: "left" }} data-testid="db-reactivation-estimate">
                      <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#5A6478", fontWeight: 700, fontFamily: FONT_MONO, marginBottom: 6 }}>
                        Estimated 30-day recovery
                      </div>
                      <div style={{ fontFamily: FONT_DISPLAY, fontSize: 32, fontWeight: 900, color: T.coral, letterSpacing: "-0.02em", lineHeight: 1 }}>
                        ${estimate.revenue_lo.toLocaleString()}<span style={{ fontSize: 18, color: "#94A3B8" }}> – </span>${estimate.revenue_hi.toLocaleString()}
                      </div>
                      <div style={{ fontSize: 12.5, color: "#3D4756", marginTop: 8, lineHeight: 1.45 }}>
                        {estimate.rebooks_lo}–{estimate.rebooks_hi} rebooked customers × ${estimate.avg_ticket.toLocaleString()} avg ticket · {estimate.rebook_rate_lo_pct}–{estimate.rebook_rate_hi_pct}% rate typical for {estimate.industry_used}
                      </div>
                    </div>
                    <p style={{ fontSize: 13, color: "#3D4756", marginTop: 14, lineHeight: 1.55 }}>
                      Full breakdown + first-touch SMS example are in your inbox now. Watch for 4 more short follow-ups over the next 14 days.
                    </p>
                  </>
                ) : (
                  <p style={{ fontSize: 14, color: "#3D4756", marginTop: 12, lineHeight: 1.55 }}>
                    Personalized revenue estimate is landing in your inbox now.
                  </p>
                )}
              </div>
            ) : (
              <>
                <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: T.coralDark, fontWeight: 700, fontFamily: FONT_MONO }}>
                  Free reactivation estimate
                </div>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: 28, fontWeight: 500, lineHeight: 1.15, margin: "10px 0 8px", color: T.ink }}>
                  How much is <em style={{ fontStyle: "italic", color: T.coral, fontWeight: 400 }}>hiding</em> in your list?
                </h3>
                <p style={{ fontSize: 13.5, color: "#3D4756", margin: "0 0 22px", lineHeight: 1.55 }}>
                  Tell us your industry + list size and we&apos;ll email a live revenue estimate + sample first-touch SMS in 60 seconds.
                </p>
                <form onSubmit={submit} style={{ display: "grid", gap: 12 }} data-testid="db-reactivation-form">
                  <input
                    required value={f.business} onChange={(e) => setF({ ...f, business: e.target.value })}
                    placeholder="Business name" data-testid="db-reactivation-business"
                    style={{ padding: "13px 16px", fontSize: 14, background: "#fff", border: `1px solid ${T.line}`, outline: "none", color: T.ink }}
                  />
                  <input
                    required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                    placeholder="you@yourbusiness.com" data-testid="db-reactivation-email"
                    style={{ padding: "13px 16px", fontSize: 14, background: "#fff", border: `1px solid ${T.line}`, outline: "none", color: T.ink }}
                  />
                  <select
                    value={f.industry} onChange={(e) => setF({ ...f, industry: e.target.value })}
                    data-testid="db-reactivation-industry"
                    style={{ padding: "13px 16px", fontSize: 14, background: "#fff", border: `1px solid ${T.line}`, outline: "none", color: T.ink, appearance: "none" }}
                  >
                    <option value="">Industry…</option>
                    {INDUSTRY_OPTIONS.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                    <option value="Other">Other / not listed</option>
                  </select>
                  <select
                    value={f.size} onChange={(e) => setF({ ...f, size: e.target.value })}
                    data-testid="db-reactivation-size"
                    style={{ padding: "13px 16px", fontSize: 14, background: "#fff", border: `1px solid ${T.line}`, outline: "none", color: T.ink, appearance: "none" }}
                  >
                    <option value="">Rough list size…</option>
                    <option value="<500">Under 500 contacts</option>
                    <option value="500-2000">500 – 2,000 contacts</option>
                    <option value="2000-5000">2,000 – 5,000 contacts</option>
                    <option value="5000+">5,000+ contacts</option>
                    <option value="unsure">Not sure yet</option>
                  </select>
                  <button
                    type="submit" disabled={busy || (!!TURNSTILE_SITE_KEY && !turnstileToken)} data-testid="db-reactivation-submit"
                    style={{ background: T.coral, color: T.cream, border: "none", padding: "14px 22px", fontSize: 15, fontWeight: 600, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.7 : 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                  >
                    {busy ? "Calculating…" : "Get my revenue estimate →"}
                  </button>
                  {TURNSTILE_SITE_KEY && (
                    <div ref={turnstileRef} data-testid="db-reactivation-turnstile" style={{ margin: "8px 0 0", display: "flex", justifyContent: "center" }} />
                  )}
                  <p style={{ fontSize: 11, color: "#5A6478", textAlign: "center", margin: "6px 0 0", lineHeight: 1.4 }}>
                    No card · Instant email · Unsubscribe anytime
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
